import type { RelevantContactResponseDto } from "@shared/generated/crm/models/RelevantContactResponseDto";
import type { FocusLeadSignal } from "./focusLeadSignal";

const initialsFromDisplayName = (displayName: string): string | null => {
  const trimmed = displayName.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = Array.from(parts[0])[0];
    const last = Array.from(parts[parts.length - 1])[0];
    return `${first ?? ""}${last ?? ""}`.toUpperCase() || null;
  }
  const chars = Array.from(trimmed);
  if (chars.length >= 2) {
    return `${chars[0]}${chars[1]}`.toUpperCase();
  }
  return chars[0] ? `${chars[0]}`.toUpperCase() : null;
};

const displayNameFromDto = (dto: RelevantContactResponseDto): string => {
  if (dto.fullName?.trim()) return dto.fullName.trim();
  const firstOrPreferred = dto.preferredName?.trim() || dto.firstName?.trim();
  const fromParts = [firstOrPreferred, dto.lastName?.trim()]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (fromParts) return fromParts;
  return "Unknown contact";
};

const priorityFromDto = (
  dto: RelevantContactResponseDto,
): FocusLeadSignal["priority"] => {
  const stage = dto.leadStage?.toLowerCase();
  if (!stage) return "low";
  if (stage.includes("qualified") || stage.includes("hot")) return "high";
  if (stage.includes("lost") || stage.includes("closed")) return "low";
  return "medium";
};

const leadStageFromDto = (dto: RelevantContactResponseDto): string => {
  const leadStage = dto.leadStage?.trim();
  return leadStage || "Unknown";
};

const intentRoleFromDto = (
  dto: RelevantContactResponseDto,
): FocusLeadSignal["intentRole"] => {
  const intent = dto.leadIntent?.trim().toUpperCase();

  if (intent === "BUY") return "buyer";
  if (intent === "SELL") return "seller";
  if (intent === "RENT") return "renter";
  if (intent === "RENT_OUT") return "landlord";

  return "unknown";
};

export const mapRelevantContactToFocusLead = (
  dto: RelevantContactResponseDto,
): FocusLeadSignal => {
  const displayName = displayNameFromDto(dto);
  return {
    id: dto.leadId,
    contactId: dto.contactId,
    displayName,
    initials: initialsFromDisplayName(displayName),
    leadStage: leadStageFromDto(dto),
    intentRole: intentRoleFromDto(dto),
    signalSummary: dto.recentActivity,
    priority: priorityFromDto(dto),
  };
};
